import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useViewport } from "../../../../utils/useViewport";
import AllBFCReport from "../../../BFCReport/AllBFCReport";
import BFCReportPdf from "../../../BFCReport/BFCReportPdf";
import MetricTable from "../MetricTable";
import { rankConsultantGroups, shapeConsultantTable } from "../consultantTable";
import { fmt, rangeLabelFor } from "../useReportFilter";
import {
  CORE_METRIC_IDS,
  MetricGroup,
  coreMetricsFromCatalog,
  metricNumber,
  useMetrics,
} from "../useMetrics";
import { ConsultantOption, TabProps } from "../types";
import ConsultantStatsRow from "./ConsultantStatsRow";
import CoreMetricChart, { CoreMetric } from "./CoreMetricChart";
import ReportModal from "./ReportModal";

const PRIMARY = "#9D2362";
const MAGENTA = "#9E1F63FF";
const CARD_BORDER = "1px solid #ececf0";
const PROGRAM_TOTAL = "Program total";

const reportButtonSx = {
  backgroundColor: MAGENTA,
  fontWeight: 700,
  "&:hover": { backgroundColor: "#7a184c" },
};

/** What BFCReportPdf reads off `props.user`. */
interface ReportUser {
  _id: string;
  firstName: string;
  lastName: string;
}

interface ProgramStatsSectionProps extends TabProps {
  isAdmin: boolean;
  /** Every consultant, loaded by the layout for admins only. */
  consultants: ConsultantOption[];
  currentUser: any;
}

const hasCoreActivity = (group: MetricGroup) =>
  CORE_METRIC_IDS.some((id) => metricNumber(group.metrics, id) !== 0);

/**
 * The old Program Statistics page, rebuilt on the hub's metrics: the program
 * total chart, one chart per consultant, the Stats Report table and the Detail
 * Report PDFs. One `/reports/metrics` call (`groupBy=consultant` +
 * `programTotal=1`) feeds every chart and the table. Admins see every
 * consultant; everyone else sees the program total and their own numbers.
 */
const ProgramStatsSection: React.FC<ProgramStatsSectionProps> = ({
  start,
  end,
  consultant,
  onDrill,
  isAdmin,
  consultants,
  currentUser,
}) => {
  const { isDesktop } = useViewport();
  const { data, loading, error } = useMetrics({
    start,
    end,
    consultant,
    groupBy: "consultant",
    programTotal: true,
  });
  const [statsOpen, setStatsOpen] = useState(false);
  const [allReportOpen, setAllReportOpen] = useState(false);
  const [userReportOpen, setUserReportOpen] = useState(false);
  // Kept after the modal closes so its title does not blank while fading out.
  const [reportUser, setReportUser] = useState<ReportUser | null>(null);

  const ownId = String(currentUser?._id || "");
  // The columns shown do not add up to the program: a non-admin's own, or the
  // one consultant an admin picked in the filter bar.
  const scoped = !isAdmin || Boolean(consultant);

  const metrics: CoreMetric[] = useMemo(
    () => coreMetricsFromCatalog(data?.catalog),
    [data],
  );

  // A server that has not shipped `programTotal` yet: `total` is the same
  // thing only for an admin with no consultant selected. Otherwise there is
  // no program total to draw.
  const programTotal = data
    ? data.programTotal || (isAdmin && !consultant ? data.total : null)
    : null;

  // `groups` only carry a key and a label; titles and e-mails come from the
  // admin's consultant list and, for the caller, from the login itself.
  const profiles = useMemo(() => {
    const byId = new Map<string, ConsultantOption>();
    consultants.forEach((option) => byId.set(String(option._id), option));
    if (ownId && !byId.has(ownId)) {
      byId.set(ownId, {
        _id: ownId,
        name: `${currentUser?.firstName || ""} ${
          currentUser?.lastName || ""
        }`.trim(),
        firstName: currentUser?.firstName || "",
        lastName: currentUser?.lastName || "",
        email: currentUser?.email,
        role: currentUser?.role,
      });
    }
    return byId;
  }, [consultants, currentUser, ownId]);

  const { rows, hiddenCount } = useMemo(() => {
    if (!data) return { rows: [] as MetricGroup[], hiddenCount: 0 };
    if (!isAdmin) {
      // Their own row, always — the server scopes `groups` and `total` to
      // the caller, so `total` stands in if it sent no group for them.
      const own = data.groups.find((g) => String(g.key) === ownId) ||
        data.groups[0] || {
          key: ownId,
          label: profiles.get(ownId)?.name || "You",
          metrics: data.total,
        };
      return { rows: [own], hiddenCount: 0 };
    }
    const ranked = rankConsultantGroups(data.groups);
    // A consultant picked in the filter bar is shown whatever their numbers.
    if (consultant) return { rows: ranked, hiddenCount: 0 };
    const active = ranked.filter(hasCoreActivity);
    return { rows: active, hiddenCount: ranked.length - active.length };
  }, [data, isAdmin, consultant, ownId, profiles]);

  // The Stats Report: the Consultants tab's table over the same response.
  const tableData = useMemo(() => {
    if (!data) return null;
    const shaped = shapeConsultantTable(data);
    return {
      ...shaped,
      groups: isAdmin ? shaped.groups : rows,
      total: programTotal && scoped ? programTotal : shaped.total,
    };
  }, [data, isAdmin, rows, programTotal, scoped]);

  const openUserReport = useCallback(
    (id: string, label: string) => {
      const known = profiles.get(String(id));
      setReportUser({
        _id: String(id),
        firstName: known ? known.firstName : label,
        lastName: known ? known.lastName : "",
      });
      setUserReportOpen(true);
    },
    [profiles],
  );

  const openGroupReport = useCallback(
    (group: MetricGroup) => openUserReport(group.key, group.label),
    [openUserReport],
  );

  const drillConsultant = useCallback(
    (metric: CoreMetric, group: MetricGroup) =>
      onDrill({
        id: metric.id,
        label: metric.label,
        consultant: group.key,
        consultantLabel: group.label,
      }),
    [onDrill],
  );

  const drillProgram = (metric: CoreMetric) =>
    onDrill({
      id: metric.id,
      label: metric.label,
      orgWide: true,
      consultantLabel: PROGRAM_TOTAL,
    });

  const rangeKey = `${fmt(start)}_${fmt(end)}`;
  const reportUserName = reportUser
    ? `${reportUser.firstName} ${reportUser.lastName}`.trim()
    : "";

  return (
    <Box sx={{ mt: 3, textAlign: "left" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ mb: 2, gap: 1 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#2c2c34" }}>
            Program Statistics
          </Typography>
          <Typography variant="body2" sx={{ color: "#8a8a93" }}>
            {rangeLabelFor({ start, end })} ·{" "}
            {isAdmin
              ? "Click a bar to see the records behind it."
              : "Click a bar in your own chart to see the records behind it."}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            disabled={!data}
            onClick={() => setStatsOpen(true)}
            sx={reportButtonSx}
          >
            Stats Report
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              isAdmin
                ? setAllReportOpen(true)
                : openUserReport(ownId, profiles.get(ownId)?.name || "")
            }
            sx={reportButtonSx}
          >
            Detail Report
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !data && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={24} sx={{ color: PRIMARY }} />
        </Stack>
      )}

      {data && (
        <Stack spacing={2}>
          {programTotal && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: CARD_BORDER,
                backgroundColor: "white",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: "#2c2c34",
                  borderLeft: `4px solid ${PRIMARY}`,
                  pl: 1.5,
                  mt: 2,
                  ml: 2,
                }}
              >
                {PROGRAM_TOTAL}
              </Typography>
              {/* The records endpoint only returns a non-admin their own
                  rows, which would not add up to these bars: not clickable. */}
              <CoreMetricChart
                metrics={metrics}
                bucket={programTotal}
                wideLabels={isDesktop}
                onMetricClick={isAdmin ? drillProgram : undefined}
              />
            </Paper>
          )}

          {rows.map((group) => (
            <ConsultantStatsRow
              key={group.key}
              group={group}
              profile={profiles.get(String(group.key))}
              metrics={metrics}
              wideLabels={isDesktop}
              onMetricClick={drillConsultant}
              onDetailReport={openGroupReport}
            />
          ))}

          {rows.length === 0 && hiddenCount === 0 && (
            <Typography sx={{ color: "#8a8a93" }}>
              No consultants to show for this range.
            </Typography>
          )}
          {hiddenCount > 0 && (
            <Typography variant="caption" sx={{ color: "#8a8a93" }}>
              {hiddenCount === 1
                ? "1 consultant with no activity in this range is hidden."
                : `${hiddenCount} consultants with no activity in this range are hidden.`}
            </Typography>
          )}
        </Stack>
      )}

      <ReportModal
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        title="Stats Report"
      >
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2" sx={{ color: "#8a8a93", mb: 1 }}>
            {rangeLabelFor({ start, end })}
          </Typography>
          {/* A drawer opens behind a modal, so every click that leads
              somewhere else closes this one first. */}
          <MetricTable
            data={tableData}
            exportName="program-statistics"
            maxHeight={440}
            totalColumn={
              programTotal && scoped
                ? { label: PROGRAM_TOTAL, clickable: isAdmin }
                : undefined
            }
            onGroupClick={(group) => {
              setStatsOpen(false);
              openGroupReport(group);
            }}
            onCellClick={(row, group) => {
              setStatsOpen(false);
              onDrill(
                group
                  ? {
                      id: row.id,
                      label: row.label,
                      consultant: group.key,
                      consultantLabel: group.label,
                    }
                  : {
                      id: row.id,
                      label: row.label,
                      orgWide: Boolean(programTotal),
                      consultantLabel: programTotal ? PROGRAM_TOTAL : undefined,
                    },
              );
            }}
          />
        </Box>
      </ReportModal>

      {/* The PDFs fetch on mount: mounted only while open, keyed by user +
          range so a reopen never shows the previous report. */}
      <ReportModal
        open={allReportOpen}
        onClose={() => setAllReportOpen(false)}
        title="All SBC Detail Report"
        placeholder="Preparing the report…"
      >
        {allReportOpen && (
          <AllBFCReport key={rangeKey} startDate={start} endDate={end} />
        )}
      </ReportModal>
      <ReportModal
        open={userReportOpen}
        onClose={() => setUserReportOpen(false)}
        title={`${reportUserName} Report`.trim()}
        placeholder="Preparing the report…"
      >
        {userReportOpen && reportUser && (
          <BFCReportPdf
            key={`${reportUser._id}-${rangeKey}`}
            user={reportUser}
            startDate={start}
            endDate={end}
          />
        )}
      </ReportModal>
    </Box>
  );
};

export default ProgramStatsSection;
